import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Coins, Calendar, Music, QrCode, Globe, Bell, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { seoConfig } from '../config/seo';

const AppPage = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Coins,
      title: 'Invasion Coins',
      description: 'Gagne des points à chaque événement et échange-les contre des avantages exclusifs: boissons, accès VIP, merchandising.'
    },
    {
      icon: Calendar,
      title: 'Réservations',
      description: 'Réserve ta table VIP directement depuis l\'app. Reçois une confirmation instantanée et gère tes réservations.'
    },
    {
      icon: Music,
      title: 'DJ Song Requests',
      description: 'Envoie tes demandes de titres au DJ en live. Vote pour les morceaux proposés par la communauté.'
    },
    {
      icon: QrCode,
      title: 'QR Attendance',
      description: 'Scanne ton QR code à l\'entrée pour valider ta présence et gagner automatiquement des Invasion Coins.'
    },
    {
      icon: Globe,
      title: 'Multi-langue',
      description: 'L\'app est disponible en français, néerlandais, anglais et espagnol pour toute la familia.'
    },
    {
      icon: Bell,
      title: 'Notifications',
      description: 'Reçois les alertes pour les nouveaux événements, les ouvertures de billetterie et les offres exclusives.'
    },
  ];

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-32" data-testid="app-page">
      <SEO {...seoConfig.app} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl sm:text-6xl tracking-tight text-[#F5F0E8] mb-3"
          >
            {t('app.title')}
          </motion.h1>
          <p className="font-serif italic text-xl text-[#B9B2A8]">
            {t('app.subtitle')}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center order-2 lg:order-1"
          >
            <div className="relative">
              {/* Phone Frame */}
              <div className="w-72 sm:w-80 aspect-[9/19] bg-[#1A1A1A] rounded-[3rem] p-3 shadow-2xl border border-white/10">
                <div className="w-full h-full bg-[#060606] rounded-[2.25rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-[#1A1A1A] rounded-b-2xl" />
                  {/* Screen Content */}
                  <div className="w-full h-full flex flex-col">
                    {/* Header */}
                    <div className="pt-10 pb-4 px-6 bg-gradient-to-b from-[#C9A84C]/20 to-transparent">
                      <h4 className="font-display text-lg text-[#F5F0E8] text-center">INVASION LATINA</h4>
                    </div>
                    {/* Content */}
                    <div className="flex-1 p-4 space-y-3">
                      <div className="bg-white/5 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Coins className="w-4 h-4 text-[#C9A84C]" />
                          <span className="text-xs text-[#F5F0E8]">Mes Coins</span>
                        </div>
                        <span className="font-display text-2xl text-[#C9A84C]">1,250</span>
                      </div>
                      <div className="bg-white/5 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-[#C9A84C]" />
                          <span className="text-xs text-[#F5F0E8]">Prochain événement</span>
                        </div>
                        <span className="text-sm text-[#B9B2A8]">Open Air - 11 Juillet</span>
                      </div>
                      <div className="bg-white/5 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Music className="w-4 h-4 text-[#C9A84C]" />
                          <span className="text-xs text-[#F5F0E8]">Song Request</span>
                        </div>
                        <span className="text-sm text-[#B9B2A8]">Envoyer une demande</span>
                      </div>
                    </div>
                    {/* Bottom Nav */}
                    <div className="h-14 border-t border-white/10 flex items-center justify-around px-4">
                      <div className="w-8 h-8 rounded-full bg-[#C9A84C]/20 flex items-center justify-center">
                        <Smartphone className="w-4 h-4 text-[#C9A84C]" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-[#B9B2A8]" />
                      </div>
                      <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                        <QrCode className="w-4 h-4 text-[#B9B2A8]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Glow */}
              <div className="absolute inset-0 -z-10 blur-3xl opacity-30 bg-gradient-to-br from-[#C9A84C] to-[#FF6A2A]" />
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="space-y-6">
              {features.slice(0, 4).map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-6 h-6 text-[#C9A84C]" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-[#F5F0E8] mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-[#B9B2A8] leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* More Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {features.slice(4).map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-[#C9A84C]/20 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-[#C9A84C]" />
              </div>
              <h3 className="font-display text-lg text-[#F5F0E8] mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-[#B9B2A8] leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Download CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center bg-white/5 border border-white/10 rounded-2xl p-8 sm:p-12"
        >
          <h2 className="font-display text-3xl text-[#F5F0E8] mb-4">
            Télécharge l'App
          </h2>
          <p className="text-[#B9B2A8] mb-8 max-w-lg mx-auto">
            Rejoins la familia et profite de tous les avantages de l'application Invasion Latina.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://apps.apple.com/us/app/invasion-latina/id6758810207"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-[#060606] rounded-xl hover:bg-[#F5F0E8] transition-colors"
              data-testid="app-page-download-ios"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs opacity-70">Télécharger sur</div>
                <div className="text-lg font-semibold">App Store</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.invasionlatina.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-[#060606] rounded-xl hover:bg-[#F5F0E8] transition-colors"
              data-testid="app-page-download-android"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs opacity-70">Disponible sur</div>
                <div className="text-lg font-semibold">Google Play</div>
              </div>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AppPage;
